<script>
  import { stringify } from 'csv-stringify/browser/esm/sync';
  import {
    Button,
    Card,
    WidgetPlaceholder,
    Spinner,
    Dropdown,
    DropdownItem,
  } from 'flowbite-svelte';
  import { FileExportSolid, FilterSolid } from 'flowbite-svelte-icons';
  import classNames from 'classnames';
  import { sendReportCsvExportDebugEvent } from '../data/debug';
  import { queryTopCuratedShortlinks } from '../data/topCuratedShortlinks';
  import { queryDailyCuratedStatsByShortlink } from '../data/dailyCuratedStats';
  import { queryCreatorCuratedPages } from '../data/creatorCuratedPages';
  import { getDailyResults } from '../data/utils';
  import { searchParams } from '../store';
  import { Heading } from '@button/ui';
  import CuratedShortlinkChart from './CuratedShortlinkChart.svelte';

  const ALL_PAGES = 'All Pages';

  $: topCuratedShortlinks = queryTopCuratedShortlinks({
    startDate: $searchParams.startDate,
    endDate: $searchParams.endDate,
    socialPlatform: $searchParams.socialPlatform,
    pageFilterPath: $searchParams.pageFilterPath,
  });
  $: topCuratedShortlinksData = $topCuratedShortlinks?.isSuccess
    ? $topCuratedShortlinks?.data
    : [];
  $: curatedShortlinkStats = queryDailyCuratedStatsByShortlink({
    pages: topCuratedShortlinksData,
    startDate: $searchParams.startDate,
    endDate: $searchParams.endDate,
    socialPlatform: $searchParams.socialPlatform,
  });
  $: curatedShortlinksStatsIsLoading = $curatedShortlinkStats?.some(
    (stats) => stats.isLoading,
  );
  $: isError = $curatedShortlinkStats?.some((stats) => stats.isError);
  $: curatedShortlinksStatsIsSuccess = $curatedShortlinkStats.every(
    (stats) => stats.isSuccess,
  );

  $: creatorCuratedPages = queryCreatorCuratedPages();
  $: creatorCuratedPagesData = $creatorCuratedPages?.isSuccess
    ? [
        ALL_PAGES,
        ...($creatorCuratedPages?.data || []).map((page) => page.path),
      ]
    : [ALL_PAGES];

  $: currentDailyResults = getDailyResults(
    $curatedShortlinkStats,
    'currentData',
  );
  $: previousDailyResults = getDailyResults(
    $curatedShortlinkStats,
    'previousData',
  );

  $: isLoading =
    curatedShortlinksStatsIsLoading || $topCuratedShortlinks?.isLoading;
  $: hasData =
    curatedShortlinksStatsIsSuccess &&
    (currentDailyResults?.length || $searchParams.pageFilterPath);

  let pageDropdownOpen = false;

  function handleClickPages(event) {
    const { innerText } = event.target;

    if (innerText === ALL_PAGES) {
      $searchParams.pageFilterPath = undefined;

      pageDropdownOpen = false;

      return;
    }

    $searchParams.pageFilterPath = innerText;
    pageDropdownOpen = false;
  }

  function handleClickExport() {
    const mappedResults = topCuratedShortlinksData.map((s) => {
      const { rank } = s;
      const {
        page,
        name,
        shortlink,
        total_mobile_journeys: totalMobileJourneys,
      } = s.curatedByPage;
      return {
        rank,
        page,
        name,
        shortlink,
        mobile_clicks: totalMobileJourneys,
      };
    });

    const csv = stringify(mappedResults, {
      header: true,
      columns: [
        { key: 'rank' },
        { key: 'page' },
        { key: 'name' },
        { key: 'shortlink' },
        { key: 'mobile_clicks' },
      ],
    });

    sendReportCsvExportDebugEvent('curated-top-shortlinks');

    // Generate a hidden element to trigger file download
    const hiddenElement = window.document.createElement('a');
    hiddenElement.href = `data:text/csv;charset=utf-8,${encodeURI(csv)}`;
    hiddenElement.target = '_blank';
    hiddenElement.download = `top_curated_shortlinks_${$searchParams.startDate}_${$searchParams.endDate}.csv`;
    hiddenElement.click();
  }
</script>

{#if isLoading || isError || hasData}
  <Card class="flex h-full w-full max-w-full flex-col">
    <div
      class="col-span-2 flex flex-col justify-end gap-4 lg:col-span-1 lg:col-start-2 lg:flex-row lg:items-center"
    >
      <Heading>Top CuratedBy Shortlinks</Heading>
      <Button
        size="sm"
        outline
        color="light"
        class="bg-transparent text-gray-800"
        ><FilterSolid class="me-2 h-3 w-3" />{$searchParams.pageFilterPath ||
          ALL_PAGES}</Button
      >
      <Dropdown bind:open={pageDropdownOpen}>
        {#each creatorCuratedPagesData as page}
          <DropdownItem on:click={handleClickPages}>{page}</DropdownItem>
        {/each}
      </Dropdown>
    </div>
    {#if isLoading}
      <div class="relative">
        <div class="absolute flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
        <WidgetPlaceholder divClass="w-full p-6" />
      </div>
    {/if}
    {#if isError}
      <div class="flex h-full flex-col items-center justify-center">
        <p class="text-gray-500">Something went wrong.</p>
        <button
          class="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          on:click={() => $topCuratedShortlinks.refetch()}
        >
          Retry
        </button>
      </div>
    {/if}
    <div
      class={classNames('flex h-full flex-col', {
        hidden: !hasData,
      })}
    >
      <CuratedShortlinkChart
        {currentDailyResults}
        {previousDailyResults}
        {topCuratedShortlinksData}
      />
      <div class="mt-4 flex flex-1 items-end">
        <Button
          on:click={handleClickExport}
          size="sm"
          color="light"
          class="w-fit"
          ><FileExportSolid class="me-2 h-3.5 w-3.5" />
          Export
        </Button>
      </div>
    </div>
  </Card>
{/if}
